import { useCurrentViewportOrientation } from '@/hooks/use-current-viewport-orientation';
import { useMediaControls } from '@rhg/hooks/use-media-controls';
import { useMediaTracker } from '@rhg/hooks/use-media-tracker';
import { MediaTrackerProps } from '@rhg/hooks/use-media-tracker/types';
import { useVideoPlayer } from '@rhg/hooks/use-video-player';
import { Box } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { MediaEvent, getM3U8ProxyUrl } from '@rhg/utils/media';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MediaEl, PlayerWithTracks } from '../types';
import { VideoControls } from './controls';
import { VideoMinimalControls } from './minimal-controls';
import { VideoPlayerControls, VideoPlayerProps } from './types';

const VideoJs = dynamic(() =>
    import('@rhg/components/media/video-player/video-js').then(
        (mod) => mod.VideoJs
    )
);

const styleVideoContainer = {
    width: '100%',
    maxHeight: '100dvh',
    position: 'relative',
    overflow: 'hidden',
};

export function VideoPlayer({
    videos,
    forcedOrientation,
    autoplay,
    muted,
    title,
    subtitle,
    showSpinner = true,
    showPoster = true,
    hasAutoplayFocusTrap = false,
    controls = VideoPlayerControls.Full,
    willTrackProgress = false,
    previousProgress = 0,
    sx,
    videoJsSx,
    actionButton,
    shouldInterruptPlay,
    loop,
    trackFn,
    onEnd,
    onControlClick,
}: VideoPlayerProps) {
    const containerRef = useRef(null);
    const [mediaEl, setMediaEl] = useState<MediaEl>();
    const [hasStartedAutoplay, setHasStartedAutoplay] = useState(false);
    const [hasAutoPlayRestriction, setHasAutoPlayRestriction] = useState(false);
    const { setAutoplayFocusTrap } = useVideoPlayer();

    const currentOrientation = useCurrentViewportOrientation();
    const preferedOrientation = forcedOrientation ?? currentOrientation;
    const video = useMemo(() => {
        return (
            videos.find((video) => video.orientation === preferedOrientation) ??
            videos[0]
        );
    }, [videos, preferedOrientation]);
    const orientation = video?.orientation ?? 'landscape';

    const { setTime } = useMediaControls({
        type: 'video',
        mediaEl: mediaEl ?? null,
    });

    useMediaTracker({
        assetType: 'video',
        mediaEl,
        trackFn: (progress: MediaTrackerProps) => {
            if (
                progress.eventType === MediaEvent.LoadedMetadata &&
                previousProgress
            ) {
                setTime(previousProgress / 1000);
            }
            trackFn?.(progress);
        },
        willTrackProgress,
    });

    const willUseM3U8Proxy =
        video?.type === 'application/x-mpegURL' &&
        video?.needsCredentials &&
        video?.credentials;

    const beforeRequest = useCallback(
        (options: { uri: string }) => {
            if (!willUseM3U8Proxy) {
                const credentials =
                    video?.needsCredentials && video?.credentials
                        ? `?${video?.credentials}`
                        : '';
                options.uri = `${options.uri}${credentials}`;
            }
            return options;
        },
        [video?.credentials, video?.needsCredentials, willUseM3U8Proxy]
    );

    const credentialSrc = useMemo(() => {
        // Return proxy URL
        if (willUseM3U8Proxy) {
            return getM3U8ProxyUrl(video.src, video?.credentials ?? '');
        }

        const credentials =
            video?.needsCredentials && video?.credentials
                ? `?${video?.credentials}`
                : '';
        return `${video.src}${credentials}`;
    }, [
        video.credentials,
        video?.needsCredentials,
        video.src,
        willUseM3U8Proxy,
    ]);

    const needsAutoplayTrigger = useMemo(
        () =>
            hasAutoplayFocusTrap &&
            autoplay &&
            mediaEl?.paused &&
            !hasStartedAutoplay &&
            document.hasFocus(),
        [autoplay, hasAutoplayFocusTrap, hasStartedAutoplay, mediaEl?.paused]
    );

    const startAutoplay = useCallback(() => {
        setHasStartedAutoplay(true);
        if (needsAutoplayTrigger) {
            setTimeout(() => {
                const autoPlayPromise = mediaEl?.play();
                if (autoPlayPromise !== undefined) {
                    autoPlayPromise
                        .then(() => {
                            setHasAutoPlayRestriction(false);
                        })
                        .catch((error) => {
                            if (error.name === 'NotAllowedError') {
                                // Handle autoplay restriction
                                setHasAutoPlayRestriction(true);
                            }
                        });
                }
            }, 100);
        }
    }, [mediaEl, needsAutoplayTrigger]);

    useEffect(() => {
        setAutoplayFocusTrap(() =>
            needsAutoplayTrigger ? startAutoplay : undefined
        );
    }, [startAutoplay, needsAutoplayTrigger, setAutoplayFocusTrap]);

    return (
        <Box ref={containerRef} sx={sxAppend(styleVideoContainer, sx)}>
            {(!video?.needsCredentials || video?.credentials) &&
                video?.src &&
                video?.type && (
                    <VideoJs
                        sx={videoJsSx}
                        src={credentialSrc}
                        type={video?.type}
                        orientation={orientation}
                        withCredentials={video?.needsCredentials}
                        options={{
                            loop,
                            autoplay: !!autoplay,
                            muted: !!muted,
                            controls: false,
                            loadingSpinner: showSpinner ? undefined : false,
                            poster: showPoster ? video?.poster : undefined,
                            beforeRequest,
                        }}
                        setMediaEl={setMediaEl}
                        onEnd={onEnd}
                    />
                )}
            {mediaEl &&
                (controls === VideoPlayerControls.Minimal ? (
                    <VideoMinimalControls
                        actionButton={actionButton}
                        mediaEl={mediaEl as PlayerWithTracks}
                        isInitiallyMuted={!!muted}
                        shouldInterruptPlay={shouldInterruptPlay}
                        onControlClick={onControlClick}
                    />
                ) : controls === VideoPlayerControls.Full ? (
                    <VideoControls
                        title={title}
                        subtitle={subtitle}
                        actionButton={actionButton}
                        fullscreenContainerRef={containerRef}
                        mediaEl={mediaEl as PlayerWithTracks}
                        isInitiallyMuted={!!muted}
                        shouldInterruptPlay={shouldInterruptPlay}
                        onControlClick={onControlClick}
                        hasAutoPlayRestriction={hasAutoPlayRestriction}
                    />
                ) : null)}
        </Box>
    );
}
